import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaFutbol, FaHandshake, FaTrophy, FaHeart, FaGraduationCap, FaRocket, FaArrowRight } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import './TeamSpirit.css';

const TeamSpirit: React.FC = () => {
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const values = [
    {
      icon: <FaFutbol />,
      title: t('teamSpirit.values.excellence.title'),
      description: t('teamSpirit.values.excellence.description'),
      color: 'var(--junior-red)'
    },
    {
      icon: <FaHandshake />,
      title: t('teamSpirit.values.teamwork.title'),
      description: t('teamSpirit.values.teamwork.description'),
      color: 'var(--junior-blue)'
    },
    {
      icon: <FaTrophy />,
      title: t('teamSpirit.values.dedication.title'),
      description: t('teamSpirit.values.dedication.description'),
      color: 'var(--junior-gold)'
    },
    {
      icon: <FaHeart />,
      title: t('teamSpirit.values.passion.title'),
      description: t('teamSpirit.values.passion.description'),
      color: 'var(--junior-red)'
    },
    {
      icon: <FaGraduationCap />,
      title: t('teamSpirit.values.development.title'),
      description: t('teamSpirit.values.development.description'),
      color: 'var(--junior-blue)'
    },
    {
      icon: <FaRocket />,
      title: t('teamSpirit.values.innovation.title'),
      description: t('teamSpirit.values.innovation.description'),
      color: 'var(--junior-gold)'
    }
  ];

  return (
    <section id="team-spirit" className="team-spirit section-lg">
      <div className="container">
        <motion.div
          className="team-spirit__header"
          initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="team-spirit__header-mark" aria-hidden />
          <h2 className="team-spirit__title">{t('teamSpirit.title')}</h2>
          <p className="team-spirit__subtitle">{t('teamSpirit.subtitle')}</p>
        </motion.div>

        <div className="values-grid">
          {values.map((value, index) => (
            <motion.div
              key={index}
              className="value-card"
              initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={isMobile ? { y: 0 } : { y: -5 }}
            >
              <div 
                className="value-card__icon"
                style={{ color: value.color }}
              >
                {value.icon}
              </div>
              <h3 className="value-card__title">{value.title}</h3>
              <p className="value-card__description">{value.description}</p>
              <div 
                className="value-card__accent"
                style={{ backgroundColor: value.color }}
              ></div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="team-spirit__philosophy"
          initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="philosophy-content">
            <div className="philosophy-text">
              <h3>{t('teamSpirit.philosophy.title')}</h3>
              <p>
                {t('teamSpirit.philosophy.text1')}
              </p>
              <p>
                {t('teamSpirit.philosophy.text2')}
              </p>
              <div className="philosophy-stats">
                <div className="philosophy-stat">
                  <span className="stat-number">95%</span>
                  <span className="stat-label">{t('teamSpirit.philosophy.stats.retention')}</span>
                </div>
                <div className="philosophy-stat">
                  <span className="stat-number">85%</span>
                  <span className="stat-label">{t('teamSpirit.philosophy.stats.academic')}</span>
                </div>
                <div className="philosophy-stat">
                  <span className="stat-number">100%</span>
                  <span className="stat-label">{t('teamSpirit.philosophy.stats.fun')}</span>
                </div>
              </div>
            </div>
            <div className="philosophy-visual">
              <div className="philosophy-emblem">
                <div className="philosophy-emblem__halo" aria-hidden />
                <div className="philosophy-emblem__orbit" aria-hidden />
                <div className="philosophy-emblem__face">
                  <span className="philosophy-emblem__topline" aria-hidden />
                  <div className="philosophy-emblem__inner">
                    <div className="philosophy-emblem__icon-wrap">
                      <FaFutbol className="philosophy-emblem__icon" aria-hidden />
                    </div>
                    <span className="philosophy-emblem__title">{t('philosophy.circle.title')}</span>
                    <span className="philosophy-emblem__divider" aria-hidden />
                    <span className="philosophy-emblem__subtitle">{t('philosophy.circle.subtitle')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="team-spirit__cta"
          initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          whileInView={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <h3>{t('teamSpirit.cta.title')}</h3>
          <p>{t('teamSpirit.cta.subtitle')}</p>
          <button
            type="button"
            className="team-spirit__cta-button"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="team-spirit__cta-button-label">{t('teamSpirit.cta.button')}</span>
            <FaArrowRight className="team-spirit__cta-button-icon" aria-hidden />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default TeamSpirit;
